<template>
  <transition name="ms-fade">
    <div v-if="current" class="ms-overlay" @click.self="close">
      <div class="ms" role="dialog" :aria-label="current.title">
        <div class="ms-confetti" aria-hidden="true">
          <i v-for="p in pieces" :key="p.i" :style="p.style"></i>
        </div>
        <div class="ms-emoji">{{ current.emoji }}</div>
        <h2>{{ current.title }}</h2>
        <p>{{ current.text }}</p>
        <p v-if="savedLabel && current.showSaved" class="ms-saved">A IA já te poupou cerca de <b>{{ savedLabel }}</b> de digitação.</p>
        <div class="ms-actions">
          <button type="button" class="ms-primary" @click="go(current.cta)">{{ current.cta.label }}</button>
          <button v-if="current.secondary" type="button" class="ms-link" @click="go(current.secondary)">
            {{ current.secondary.label }}
          </button>
        </div>
        <button type="button" class="ms-later" @click="close">Agora não</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { track } from "@/utils/analytics";
import { useNudge, modalShownThisLoad, markModalShown } from "@/utils/nudges";

const PENDING_KEY = "ordexa/milestone-pending";

const COLORS = ["#29b1cc", "#127a8f", "#f6a623", "#ff6b3d", "#7ed6a5", "#ffd84d"];

// Activation milestones reported by the API (first real patient, first note...): a short celebration and
// the one next step that matters at that point.
export default {
  name: "ComponentMilestone",
  data() {
    return { current: null, customerId: null, customerName: "", key: null, pieces: [] };
  },
  computed: {
    treatment() {
      return (this.$store.getters["Companies/customerTreatmentSingular"] || "paciente").toLowerCase();
    },
    treatmentPlural() {
      return (this.$store.getters["Companies/customerTreatmentPlural"] || "pacientes").toLowerCase();
    },
    savedLabel() {
      const c = this.$store.state.Companies.company || {};
      const m = Number(c.ai_minutes_saved_total) || 0;
      if (m < 5) return "";
      if (m < 60) return `${m} minutos`;
      const h = Math.floor(m / 60), r = m % 60;
      return r ? `${h}h${String(r).padStart(2, "0")}` : `${h} ${h === 1 ? "hora" : "horas"}`;
    },
  },
  mounted() {
    this.$eventbus.on("Milestone/show", this.show);
    // A celebration that had to wait (another modal came first) shows on the next page load.
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
      if (pending && pending.key) {
        localStorage.removeItem(PENDING_KEY);
        setTimeout(() => this.show(pending), 2500);
      }
    } catch (e) {
      /* ignore */
    }
  },
  beforeUnmount() {
    this.$eventbus.off("Milestone/show", this.show);
  },
  methods: {
    content(key) {
      const t = this.treatment;
      const all = {
        first_patient: {
          emoji: "🎉",
          title: `Primeiro ${t} cadastrado!`,
          text: "O histórico agora fica num lugar só. Falta o passo que mais poupa tempo: a primeira anotação. Fale uns 30 segundos sobre o último atendimento e a IA escreve.",
          cta: { label: "🎙️ Ditar a primeira anotação", action: "quick_note" },
        },
        first_note: {
          emoji: "🎉",
          title: "Primeira anotação salva!",
          text: "Seu prontuário começou. Depois de cada atendimento, é só ditar: em menos de um minuto a anotação fica pronta.",
          showSaved: true,
          cta: { label: "📅 Marcar o próximo atendimento", route: { name: "Agenda", query: { new: 1 } } },
          secondary: { label: `Trazer meus outros ${this.treatmentPlural} de uma vez`, route: { name: "DataImport" } },
        },
        third_note: {
          emoji: "🔥",
          title: "3 anotações: você pegou o ritmo!",
          text: `Quanto mais ${this.treatmentPlural} estiverem aqui, mais fácil retomar cada caso antes da sessão.`,
          showSaved: true,
          cta: { label: `Trazer o resto dos ${this.treatmentPlural}`, route: { name: "DataImport" } },
          secondary: { label: "🎙️ Ditar outro atendimento", action: "quick_note" },
        },
        tenth_note: {
          emoji: "🏆",
          title: "10 anotações no Ordexa!",
          text: "Seu prontuário já tem história. Veja no painel quanto tempo a IA te poupou até agora.",
          showSaved: true,
          cta: { label: "Ver meu painel", route: { name: "Dashboard" } },
        },
        first_appointment: {
          emoji: "📅",
          title: "Primeiro atendimento na agenda!",
          text: `Mande o lembrete pelo WhatsApp com 1 clique e ative o link para o ${t} marcar sozinho nos seus horários.`,
          cta: { label: "Ativar meu link de agendamento", route: { name: "Agenda", query: { setup: "booking" } } },
        },
      };
      return all[key];
    },
    show({ key, customerId, customerName } = {}) {
      const content = this.content(key);
      if (!content) return;
      if (modalShownThisLoad()) {
        try {
          localStorage.setItem(PENDING_KEY, JSON.stringify({ key, customerId, customerName }));
        } catch (e) {
          /* ignore */
        }
        return;
      }
      markModalShown();
      this.key = key;
      this.customerId = customerId || null;
      this.customerName = customerName || "";
      this.current = content;
      this.pieces = this.confetti();
      useNudge();
      track("milestone_shown", { milestone: key });
    },
    confetti() {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return [];
      return Array.from({ length: 28 }, (_, i) => ({
        i,
        style: {
          left: `${Math.round(Math.random() * 100)}%`,
          background: COLORS[i % COLORS.length],
          animationDelay: `${(Math.random() * 0.4).toFixed(2)}s`,
          animationDuration: `${(1.4 + Math.random()).toFixed(2)}s`,
          transform: `rotate(${Math.round(Math.random() * 360)}deg)`,
        },
      }));
    },
    go(target) {
      track("milestone_cta", { milestone: this.key, target: target.action || target.route.name });
      const { customerId, customerName } = this;
      this.current = null;
      if (target.action === "quick_note")
        this.$eventbus.emit("QuickNote/open", { source: `milestone_${this.key}`, customerId, customerName });
      else this.$router.push(target.route);
    },
    close() {
      track("milestone_dismissed", { milestone: this.key });
      this.current = null;
    },
  },
};
</script>

<style scoped>
.ms-overlay { position: fixed; inset: 0; z-index: 960; background: rgba(16, 42, 51, 0.4); display: flex; align-items: center; justify-content: center; padding: 16px; }
.ms { position: relative; overflow: hidden; width: 420px; max-width: 100%; background: #fff; border-radius: 18px; box-shadow: 0 20px 50px rgba(16, 42, 51, 0.3); padding: 26px 24px 16px; text-align: center; box-sizing: border-box; }
.ms-emoji { font-size: 2.6em; line-height: 1; margin-bottom: 6px; animation: ms-pop 0.5s ease-out; }
.ms h2 { margin: 0 0 8px; font-size: 1.3em; color: #1d2b2f; }
.ms p { margin: 0 0 10px; color: #3d5056; line-height: 1.5; }
.ms-saved { background: #f0fbfd; border-radius: 10px; padding: 8px 10px; }
.ms-actions { display: flex; flex-direction: column; align-items: center; gap: 6px; margin-top: 14px; }
.ms-primary { background: #127a8f; color: #fff; border: none; border-radius: 10px; padding: 0.75em 1.4em; font: inherit; font-weight: 700; cursor: pointer; }
.ms-link { background: none; border: none; color: #127a8f; font: inherit; font-weight: 600; cursor: pointer; padding: 6px; }
.ms-later { background: none; border: none; color: #8a999d; font: inherit; font-size: 0.9em; cursor: pointer; margin-top: 4px; }
.ms-confetti { position: absolute; inset: 0; pointer-events: none; }
.ms-confetti i { position: absolute; top: -12px; width: 8px; height: 14px; border-radius: 2px; opacity: 0; animation-name: ms-fall; animation-timing-function: ease-in; animation-fill-mode: forwards; }
@keyframes ms-fall { 0% { opacity: 1; top: -12px; } 100% { opacity: 0; top: 105%; margin-left: 30px; } }
@keyframes ms-pop { 0% { transform: scale(0.4); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
.ms-fade-enter-active, .ms-fade-leave-active { transition: opacity 0.2s; }
.ms-fade-enter-from, .ms-fade-leave-to { opacity: 0; }
</style>
